import { useState, useEffect } from "react";
import { toast } from "react-toastify";
import {
  Layers,
  Zap,
  Droplets,
  Flame,
  Wifi,
  Smartphone,
  SmartphoneNfc,
  Car,
  Building2,
  Tv,
  Landmark,
  Banknote,
  CreditCard,
  ShieldCheck,
  GraduationCap,
  Save,
} from "lucide-react";
import {
  getPropertyBbpsCategories,
  updatePropertyBbpsCategories,
} from "../../services/bbpsAdminService";
import Button from "../ui/Button";

const ICON_MAP = {
  Zap,
  Droplets,
  Flame,
  Wifi,
  Smartphone,
  SmartphoneNfc,
  Car,
  Building2,
  Tv,
  Landmark,
  Banknote,
  CreditCard,
  ShieldCheck,
  GraduationCap,
};

const SwitchToggle = ({ checked, onChange }) => (
  <button
    type="button"
    onClick={() => onChange(!checked)}
    style={{
      width: 36,
      height: 20,
      borderRadius: 10,
      background: checked ? "#2563eb" : "#cbd5e1",
      border: "none",
      cursor: "pointer",
      position: "relative",
      padding: 0,
      transition: "background 0.2s ease",
      outline: "none",
      flexShrink: 0,
    }}
  >
    <div
      style={{
        width: 16,
        height: 16,
        borderRadius: "50%",
        background: "#ffffff",
        position: "absolute",
        top: 2,
        left: checked ? 18 : 2,
        transition: "left 0.2s ease",
        boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
      }}
    />
  </button>
);

export default function PropertyBbpsSettingsSection({ societyId, isIndustrial }) {
  const [useGlobalDefaults, setUseGlobalDefaults] = useState(true);
  const [categories, setCategories] = useState([]);
  const [enabledIds, setEnabledIds] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (societyId) loadSettings();
  }, [societyId]);

  const loadSettings = async () => {
    setLoading(true);
    try {
      const data = await getPropertyBbpsCategories(societyId);
      if (data?.success) {
        setUseGlobalDefaults(data.use_global_defaults ?? true);
        setCategories(data.categories || []);
        setEnabledIds(new Set(data.enabled_categories || []));
      }
    } catch (err) {
      console.error(err);
      toast.error("Failed to load property BBPS settings");
    } finally {
      setLoading(false);
    }
  };

  const handleToggleCategory = (catId) => {
    if (useGlobalDefaults) return;
    const next = new Set(enabledIds);
    if (next.has(catId)) next.delete(catId);
    else next.add(catId);
    setEnabledIds(next);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        use_global_defaults: useGlobalDefaults,
        enabled_categories: Array.from(enabledIds),
      };
      const res = await updatePropertyBbpsCategories(societyId, payload);
      if (res?.success) {
        toast.success("Property BBPS categories saved successfully!");
        loadSettings();
      }
    } catch (err) {
      console.error(err);
      toast.error("Failed to save property BBPS categories");
    } finally {
      setSaving(false);
    }
  };

  const activeCount = useGlobalDefaults
    ? categories.filter((c) => c.is_globally_allowed).length
    : categories.filter((c) => c.is_globally_allowed && enabledIds.has(c.id)).length;

  if (loading) {
    return <div style={{ fontSize: 13, color: "#64748b" }}>Loading BBPS utility options...</div>;
  }

  return (
    <div
      style={{
        background: "#ffffff",
        border: "1px solid #e2e8f0",
        borderRadius: 10,
        padding: 24,
        marginBottom: 24,
      }}
    >
      {/* Header */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 12,
          marginBottom: 16,
          paddingBottom: 14,
          borderBottom: "1px solid #f1f5f9",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <Layers size={18} color="#2563eb" />
          <span style={{ fontSize: 15, fontWeight: 700, color: "#0f172a" }}>
            Bharat BillPay (BBPS) Available Utility Categories
          </span>
          <span
            style={{
              fontSize: 11,
              fontWeight: 600,
              padding: "2px 8px",
              background: "#eff6ff",
              color: "#1d4ed8",
              borderRadius: 12,
            }}
          >
            {activeCount} Active on Mobile
          </span>
        </div>

        <Button
          onClick={handleSave}
          loading={saving}
          icon={<Save size={15} />}
          size="sm"
          style={{ background: "var(--color-dark-bg, #27306f)", color: "#fff" }}
        >
          Save BBPS Options
        </Button>
      </div>

      {/* Mode Toggle: Global Inherit vs Custom Override */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "14px 16px",
          background: useGlobalDefaults ? "#f8fafc" : "#eff6ff",
          border: "1px solid",
          borderColor: useGlobalDefaults ? "#e2e8f0" : "#bfdbfe",
          borderRadius: 8,
          marginBottom: 16,
          cursor: "pointer",
        }}
        onClick={() => setUseGlobalDefaults(!useGlobalDefaults)}
      >
        <div>
          <div style={{ fontSize: 13.5, fontWeight: 700, color: "#0f172a" }}>
            {useGlobalDefaults
              ? "Inherit Global System Defaults (Recommended)"
              : "Custom Override for this Property"}
          </div>
          <div style={{ fontSize: 12, color: "#64748b", marginTop: 2 }}>
            {useGlobalDefaults
              ? "This property automatically inherits all categories enabled in the global master settings."
              : `Customize exactly which utility services (e.g. ${isIndustrial ? "Electricity, Water, Broadband & FASTag only" : "Electricity, Gas, Housing Maintenance"}) appear in the resident app for this property.`}
          </div>
        </div>

        <SwitchToggle
          checked={useGlobalDefaults}
          onChange={(val) => setUseGlobalDefaults(val)}
        />
      </div>

      {/* Categories Checklist */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
          gap: 12,
          opacity: useGlobalDefaults ? 0.7 : 1,
        }}
      >
        {categories.map((cat) => {
          const isGloballyAllowed = cat.is_globally_allowed;
          const isChecked = useGlobalDefaults ? isGloballyAllowed : enabledIds.has(cat.id);
          const IconComp = ICON_MAP[cat.icon] || Zap;

          return (
            <div
              key={cat.id}
              onClick={() => isGloballyAllowed && handleToggleCategory(cat.id)}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "10px 12px",
                background: isChecked && isGloballyAllowed ? "#ffffff" : "#f8fafc",
                border: "1px solid",
                borderColor: isChecked && isGloballyAllowed ? "#cbd5e1" : "#e2e8f0",
                borderRadius: 8,
                cursor: useGlobalDefaults || !isGloballyAllowed ? "default" : "pointer",
                transition: "all 0.15s ease",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <div
                  style={{
                    width: 32,
                    height: 32,
                    borderRadius: 8,
                    background: `${cat.color || "#2563eb"}15`,
                    color: cat.color || "#2563eb",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <IconComp size={16} />
                </div>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: "#1e293b" }}>
                    {cat.title}
                  </div>
                  {!isGloballyAllowed && (
                    <div style={{ fontSize: 10.5, color: "#ef4444", fontWeight: 600 }}>
                      Disabled in Global Master
                    </div>
                  )}
                </div>
              </div>

              <input
                type="checkbox"
                checked={isChecked && isGloballyAllowed}
                disabled={useGlobalDefaults || !isGloballyAllowed}
                onChange={() => handleToggleCategory(cat.id)}
                style={{ width: 16, height: 16, cursor: useGlobalDefaults ? "default" : "pointer" }}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
}
